import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useUserApi } from "../../store/api/hooks/userApiHook";
import { URLS, ACTIONS } from "../../_config";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import Icon, { iconNames } from "../icons";
import LookupValues from "../getLookupValues";
import { useMenusSelector } from "../../store/selectors/userSelector";
import { LOOKUP_TYPE } from "../../_common/constant";
import { useDialog } from "../../store/api/hooks/dialogHook";
import Loader from "../loader";
import { useUserSelector } from "../../store/selectors/userSelector";
import MenuItem, { getMenuDisplayName, isMenuActive, MenuItemData } from "./_components/MenuItem";
import FloatingSubmenu from "./_components/FloatingSubmenu";

// ─── Constants ────────────────────────────────────────────────────────────────

const SIDEBAR_KEY = "sidebar-expanded";
const COLLAPSED_W = 60;
const SETTINGS_MENU_CODE = "SETUP";

interface LeftPanelProps {
  manualNavigationBlocked?: boolean;
  alertMessageForBeforeUnload?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const LeftPanel: React.FC<LeftPanelProps> = ({
  manualNavigationBlocked = false,
  alertMessageForBeforeUnload = "",
}) => {
  const { t } = useTranslation("common");
  useUserApi();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userData = useUserSelector();
  const menuDataFromRedux = useMenusSelector();
  const { confirm } = useDialog();

  // ── Sidebar toggle (persisted) ──────────────────────────────────────────────
  const [isSidebarExpanded, setIsSidebarExpanded] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(SIDEBAR_KEY);
      return v !== null ? v === "true" : true;
    } catch {
      return true;
    }
  });

  // ── Accordion expand state (expanded sidebar) ───────────────────────────────
  const [expandedMenuIds, setExpandedMenuIds] = useState<Set<number>>(new Set());

  // ── Floating panel (collapsed sidebar) ─────────────────────────────────────
  const [floatingPanel, setFloatingPanel] = useState<{
    menu: any;
    top: number;
  } | null>(null);

  // ── Original state — kept exactly as before ─────────────────────────────────
  const [selectedMenu, setSelectedMenu] = useState<any>(null);
  const [showSubmenu, setShowSubmenu] = useState<boolean>(false);
  const [isInSetupMode, setIsInSetupMode] = useState<boolean>(false);
  const [setupSubmenus, setSetupSubmenus] = useState<any[]>([]);
  const [selectedSetupSubmenu, setSelectedSetupSubmenu] = useState<any>(null);

  // ── Scroll container ref ────────────────────────────────────────────────────
  const menuScrollRef = useRef<HTMLDivElement>(null);

  // ── Disabled / deleted lists ────────────────────────────────────────────────
  const DISABLED_MENU_ITEMS = useMemo(
    () => [
      // "ORDERS_AND_FULFILLMENT",
      // "REPORTS_ADMIN",
      "POS LAYOUT",
      // "LOTTERY",
      "FULFILLMENTS",
      // "STORE_CREDIT",
      // "BUSINESS PROFILE",
      // "STORE_LOCATIONS",
      // "WAREHOUSES",
      "ONLINE_STORE",
      "PICKUP_AND_DELIVERY",
      "FULFILMENT INTEGRATIONS",
      // "TAXES",
      // "PAYMENT",
      // "TEMPLATE_PREFERENCES",
      // "RECEIPT TEMPLATES",
      "LABEL TEMPLATES",
      // "AGREEMENTS_AND_POLICIES",
      "INVOICE EMAIL AND TEXTS",
      "SHIPMENT EMAIL AND TEXTS",
      "DELIVERY EMAIL AND TEXTS",
      "PICKUP_ORDERS",
      "DELIVERY_ORDER_IN_HOUSE",
      "DELIVERY_ORDERS_SHIPMENT",
      // "RPT_EMPLOYEE_COMMISSION"
    ],
    []
  );
  const DELETED_MENU_ITEMS = useMemo((): string[] => [], []);

  const menuData = useMemo(
    () => (menuDataFromRedux && menuDataFromRedux.length > 0 ? menuDataFromRedux : []),
    [menuDataFromRedux]
  );

  // ── Browser unload guard ────────────────────────────────────────────────────
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (manualNavigationBlocked) {
        e.preventDefault();
        e.returnValue = t("navigation.leaveMessage");
        return e.returnValue;
      }
    };
    if (manualNavigationBlocked) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [manualNavigationBlocked, alertMessageForBeforeUnload]);

  // ── Navigation helper (original signature preserved) ───────────────────────
  const navigateWithConfirmation = useCallback(
    (url: string, callback?: () => void, skipConfirmation = false) => {
      const doNav = () => {
        // Clear saved product filters only when navigating TO products from a non-product page
        if (
          !location.pathname.startsWith(URLS.PRODUCTS) &&
          url.startsWith(URLS.PRODUCTS)
        ) {
          dispatch({ type: ACTIONS.PRODUCTS.CLEAR_PRODUCT_FILTERS });
        }
        if (url === "N/A") {
          navigate(URLS.DASHBOARD);
        } else if (url && url !== "#") {
          navigate(url);
        }
        callback?.();
      };
      if (manualNavigationBlocked && !skipConfirmation) {
        confirm({
          type: "warn",
          title: t("navigation.leaveTitle"),
          message: t("navigation.leaveMessage"),
          onConfirm: doNav,
        });
      } else {
        doNav();
      }
    },
    [manualNavigationBlocked, confirm, navigate, location.pathname, dispatch, t]
  );

  // ── Sidebar toggle ──────────────────────────────────────────────────────────
  const toggleSidebar = () => {
    setIsSidebarExpanded((prev) => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_KEY, String(next)); } catch {}
      if (next) setFloatingPanel(null); // close float when expanding
      return next;
    });
  };

  // ── Accordion toggle ────────────────────────────────────────────────────────
  const toggleExpanded = useCallback((id: number) => {
    setExpandedMenuIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  // ── Auto-expand ancestors of the active URL (expanded sidebar, normal mode) ─
  useEffect(() => {
    if (!isSidebarExpanded || isInSetupMode || menuData.length === 0) return;
    const toOpen = new Set<number>();
    const walk = (items: any[]) => {
      for (const item of items) {
        if (item.submenu?.length > 0) {
          if (isMenuActive(item as MenuItemData, location.pathname)) {
            toOpen.add(item.menuId);
          }
          walk(item.submenu);
        }
      }
    };
    walk(menuData);
    if (toOpen.size > 0) {
      setExpandedMenuIds((prev) => {
        const next = new Set(prev);
        toOpen.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [location.pathname, menuData, isSidebarExpanded, isInSetupMode]);

  // ── Auto-scroll to active menu item when route changes ─────────────────────
  useEffect(() => {
    if (!isSidebarExpanded || !menuScrollRef.current) return;
    // Wait for DOM to settle after accordion expansion
    const timer = setTimeout(() => {
      const activeEl = menuScrollRef.current?.querySelector<HTMLElement>(
        "[data-menu-active='true']"
      );
      if (activeEl && menuScrollRef.current) {
        const container = menuScrollRef.current;
        const containerRect = container.getBoundingClientRect();
        const elRect = activeEl.getBoundingClientRect();
        const elRelativeTop = elRect.top - containerRect.top;
        const elRelativeBottom = elRect.bottom - containerRect.top;
        if (elRelativeBottom > container.clientHeight || elRelativeTop < 0) {
          container.scrollTo({
            top: container.scrollTop + elRelativeBottom - container.clientHeight + 100,
            behavior: "smooth",
          });
        }
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [location.pathname, isSidebarExpanded]);

  // ── Auto-expand active settings submenu (settings mode) ────────────────────
  useEffect(() => {
    if (!isSidebarExpanded || !isInSetupMode || setupSubmenus.length === 0) return;
    const toOpen = new Set<number>();
    const walk = (items: any[]) => {
      for (const item of items) {
        if (item.submenu?.length > 0) {
          if (isMenuActive(item as MenuItemData, location.pathname)) {
            toOpen.add(item.menuId);
          }
          walk(item.submenu);
        }
      }
    };
    walk(setupSubmenus);
    if (toOpen.size > 0) {
      setExpandedMenuIds((prev) => {
        const next = new Set(prev);
        toOpen.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [location.pathname, setupSubmenus, isSidebarExpanded, isInSetupMode]);

  // ── Original: handleMenuClick (unchanged logic) ─────────────────────────────
  const handleMenuClick = useCallback(
    (menu: any) => {
      const executeMenuClick = () => {
        setSelectedMenu(menu);

        // Settings → enter settings mode
        if (
          menu.menuCode === SETTINGS_MENU_CODE &&
          menu.submenu &&
          menu.submenu.length > 0
        ) {
          setIsInSetupMode(true);
          setSetupSubmenus(menu.submenu);

          const available = menu.submenu.filter(
            (s: any) =>
              !DELETED_MENU_ITEMS.includes(s.menuCode) &&
              !(!s.read && !s.write && !s.delete) &&
              !DISABLED_MENU_ITEMS.includes(s.menuCode)
          );

          if (available.length > 0) {
            const first = available[0];
            setSelectedSetupSubmenu(first);

            if (first.submenu?.length > 0) {
              setShowSubmenu(true);
              const avCh = first.submenu.filter(
                (c: any) =>
                  !DELETED_MENU_ITEMS.includes(c.menuCode) &&
                  !(!c.read && !c.write && !c.delete) &&
                  !DISABLED_MENU_ITEMS.includes(c.menuCode)
              );
              const firstCh = avCh[0] ?? first.submenu[0];
              navigateWithConfirmation(firstCh.url, undefined, true);
            } else {
              setShowSubmenu(false);
              navigateWithConfirmation(first.url, undefined, true);
            }
          } else {
            setSelectedSetupSubmenu(null);
            setShowSubmenu(false);
          }
          return;
        }

        // Normal menu with children
        if (menu.submenu?.length > 0) {
          const available = menu.submenu.filter(
            (s: any) =>
              !DELETED_MENU_ITEMS.includes(s.menuCode) &&
              !(!s.read && !s.write && !s.delete) &&
              !DISABLED_MENU_ITEMS.includes(s.menuCode)
          );

          if (available.length > 0) {
            const first = available[0];
            setShowSubmenu(true);

            if (first.submenu?.length > 0) {
              const avCh = first.submenu.filter(
                (c: any) =>
                  !DELETED_MENU_ITEMS.includes(c.menuCode) &&
                  !(!c.read && !c.write && !c.delete) &&
                  !DISABLED_MENU_ITEMS.includes(c.menuCode)
              );
              const firstCh = avCh[0] ?? first.submenu[0];
              navigateWithConfirmation(firstCh.url, undefined, true);
            } else {
              navigateWithConfirmation(first.url, undefined, true);
            }
          } else {
            setShowSubmenu(true);
          }
          return;
        }

        // Leaf
        navigateWithConfirmation(menu.url, undefined, true);
      };

      if (manualNavigationBlocked) {
        confirm({
          type: "warn",
          title: t("navigation.leaveTitle"),
          message: t("navigation.leaveMessage"),
          onConfirm: executeMenuClick,
        });
      } else {
        executeMenuClick();
      }
    },
    [
      manualNavigationBlocked,
      confirm,
      navigateWithConfirmation,
      DISABLED_MENU_ITEMS,
      DELETED_MENU_ITEMS, t
    ]
  );

  // ── Original: exitSetupMode (unchanged) ────────────────────────────────────
  const exitSetupMode = () => {
    confirm({
      type: "warn",
      title: t("leftPanel.exitSettings"),
      message: t("leftPanel.exitSettingsConfirmMessage"),
      onConfirm: () => {
        setIsInSetupMode(false);
        setSetupSubmenus([]);
        setSelectedSetupSubmenu(null);
        setSelectedMenu(null);
        setShowSubmenu(false);
        navigate(URLS.DASHBOARD);
      },
    });
  };

  // ── Original: URL → active state detection (unchanged logic) ───────────────
  useEffect(() => {
    if (menuData.length === 0) return;
    const currentPath = location.pathname;
    let foundMenu: any = null;
    let bestMatchLength = 0;
    let bestMatchMenu: any = null;


    if (
      currentPath.startsWith(URLS.SETUP) ||
      currentPath.startsWith(URLS.USER) ||
      currentPath.startsWith(URLS.SETTINGS)
    ) {
      const settingsMenu = menuData.find(
        (m: any) => m.menuCode === SETTINGS_MENU_CODE
      );
      if (settingsMenu) {
        setIsInSetupMode(true);
        setSetupSubmenus(settingsMenu.submenu);

        for (const sub of settingsMenu.submenu) {
          if (sub.url === currentPath) {
            setSelectedSetupSubmenu(sub);
            if (sub.submenu?.length > 0) setShowSubmenu(true);
            return;
          }
          if (sub.submenu) {
            for (const child of sub.submenu) {
              if (child.url === currentPath) {
                setSelectedSetupSubmenu(sub);
                setShowSubmenu(true);
                return;
              }
            }
          }
        }
        return;
      }
    }

    setIsInSetupMode(false);

    const direct = menuData.find((m: any) => m.url === currentPath);
    if (direct) {
      setSelectedMenu(direct);
      setShowSubmenu(direct.submenu?.length > 0);
      return;
    }

    for (const menu of menuData) {
      if (
        menu.url &&
        menu.url !== "N/A" &&
        menu.url !== "#" &&
        currentPath.startsWith(menu.url) &&
        menu.url !== URLS.DASHBOARD &&
        menu.url.length > bestMatchLength
      ) {
        bestMatchLength = menu.url.length;
        bestMatchMenu = menu;
      }

      if (menu.submenu?.length > 0) {
        const match = menu.submenu.find((s: any) => s.url === currentPath);
        if (match) {
          foundMenu = menu;
          break;
        }

        for (const sub of menu.submenu) {
          if (
            sub.url &&
            sub.url !== "N/A" &&
            sub.url !== "#" &&
            sub.url !== URLS.DASHBOARD &&
            currentPath.startsWith(sub.url) &&
            sub.url.length > bestMatchLength
          ) {
            bestMatchLength = sub.url.length;
            bestMatchMenu = menu;
          }

          if (sub.submenu) {
            for (const child of sub.submenu) {
              if (child.url === currentPath) {
                foundMenu = menu;
                break;
              }
            }
          }
        }
      }
    }

    if (foundMenu) {
      setSelectedMenu(foundMenu);
      setShowSubmenu(foundMenu.submenu?.length > 0);
      return;
    }

    if (bestMatchMenu) {
      setSelectedMenu(bestMatchMenu);
      setShowSubmenu(bestMatchMenu.submenu?.length > 0);
    }
  }, [location.pathname, menuData]);

  // ── Collapsed sidebar: icon click → floating panel or navigate ──────────────
  const handleIconClick = useCallback(
    (menu: any, e: React.MouseEvent<HTMLButtonElement>) => {
      if (menu.submenu?.length > 0) {
        const { top } = e.currentTarget.getBoundingClientRect();
        setFloatingPanel((prev) =>
          prev?.menu.menuId === menu.menuId ? null : { menu, top }
        );
      } else if (menu.url && menu.url !== "N/A" && menu.url !== "#") {
        setFloatingPanel(null);
        navigateWithConfirmation(menu.url);
      }
    },
    [navigateWithConfirmation]
  );

  // ── Visible top-level menus ─────────────────────────────────────────────────
  const visibleMenus = useMemo(
    () =>
      menuData
        .filter((m: any) => !DELETED_MENU_ITEMS.includes(m.menuCode))
        .filter(
          (m: any) => !(m.menuCode === "HUBWALLET_AI" && !userData?.ssoKey)
        ),
    [menuData, DELETED_MENU_ITEMS, userData]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Loading
  // ─────────────────────────────────────────────────────────────────────────────

  if (menuData.length === 0) {
    return (
      <div className="left-panel w-[275px] flex-shrink-0 h-screen flex flex-col overflow-x-visible">
        <LookupValues id={Object.values(LOOKUP_TYPE)} />
        <div className="bg-primary flex gap-4 h-[90px] items-center">
          <img src="/icon.png" alt={t("leftPanel.logoAlt")} className="h-10 ml-4" />
          <h1 className="text-accent text-3xl font-semibold mr-4">hubwallet</h1>
        </div>
        <div className="flex-grow overflow-auto flex flex-col justify-center bg-gray-200">
          <Loader />
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // EXPANDED SIDEBAR — collapsible tree
  // ─────────────────────────────────────────────────────────────────────────────

  if (isSidebarExpanded) {
    return (
      <div className="left-panel w-[300px] flex-shrink-0 h-screen flex flex-col overflow-x-visible">
        <LookupValues id={Object.values(LOOKUP_TYPE)} />

        {/* Header */}
        <div className="bg-primary h-[90px] flex-shrink-0 flex items-center px-4 gap-3">
          <img src="/icon.png" alt={t("leftPanel.logoAlt")} className="h-11 w-11 flex-shrink-0 object-contain" />
          <h1 className="text-accent text-xl font-bold flex-grow truncate tracking-wide">
            hubwallet
          </h1>
          <button
            onClick={toggleSidebar}
            title={t("leftPanel.collapseSidebar")}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-accent transition-colors"
          >
            <Icon name="arrowleft" size={16} color="currentColor" />
          </button>
        </div>

        {/* Menu area */}
        <div ref={menuScrollRef} className="flex-grow overflow-y-auto overflow-x-hidden bg-gray-200">
          {isInSetupMode ? (
            // ── Settings mode: settings submenus as accordion tree ──────────
            <>
              {setupSubmenus
                .filter((s: any) => !DELETED_MENU_ITEMS.includes(s.menuCode))
                .map((s: any) => (
                    <MenuItem
                      key={s.menuId}
                      menu={s as MenuItemData}
                      depth={0}
                      expandedIds={expandedMenuIds}
                      toggleExpanded={toggleExpanded}
                      onNavigate={(url) => navigateWithConfirmation(url)}
                      DISABLED_MENU_ITEMS={DISABLED_MENU_ITEMS}
                      DELETED_MENU_ITEMS={DELETED_MENU_ITEMS}
                      pathname={location.pathname}
                    />
                  ))}

              {/* Exit Settings */}
              <button
                className="w-full h-[48px] flex items-center gap-3 px-3 text-red-500 hover:bg-red-50 transition-colors"
                onClick={exitSetupMode}
              >
                <span className="w-6 flex items-center justify-center">
                  <Icon name="exit-settings" size={18} color="#ef4444" />
                </span>
                <span className="text-sm">{t("leftPanel.exitSettings")}</span>
              </button>
            </>
          ) : (
            // ── Normal mode: full accordion tree ────────────────────────────
            visibleMenus.map((menu: any) => {
              const isAIMenu = menu.menuCode === "HUBWALLET_AI";
              const isSettings = menu.menuCode === SETTINGS_MENU_CODE;

              return (
                <MenuItem
                  key={menu.menuId}
                  menu={menu as MenuItemData}
                  depth={0}
                  maxDepth={1}
                  expandedIds={expandedMenuIds}
                  toggleExpanded={toggleExpanded}
                  onNavigate={(url) => navigateWithConfirmation(url)}
                  // Settings enters its own mode instead of accordion-expanding
                  onOverrideClick={isSettings ? handleMenuClick : undefined}
                  DISABLED_MENU_ITEMS={DISABLED_MENU_ITEMS}
                  DELETED_MENU_ITEMS={DELETED_MENU_ITEMS}
                  pathname={location.pathname}
                  isAIMenu={isAIMenu}
                />
              );
            })
          )}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // COLLAPSED SIDEBAR — icon strip + floating panel
  // Preserves original settings mode icon layout exactly
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div
      className="left-panel flex-shrink-0 h-screen flex flex-col overflow-x-visible"
      style={{ width: COLLAPSED_W }}
    >
      <LookupValues id={Object.values(LOOKUP_TYPE)} />

      {/* Header — logo only */}
      <div className="bg-primary h-[90px] flex-shrink-0 flex items-center justify-center">
        <img src="/icon.png" alt={t("leftPanel.logoAlt")} className="h-12 w-12 object-contain" />
      </div>

      {/* Icon list */}
      <div className="h-[calc(100vh-90px)] overflow-auto flex">
        {isInSetupMode ? (
          // ── Settings mode (original layout): settings submenu icons + sub-panel
          <>
            <div className="w-full h-full bg-gray-200">
              {/* Expand toggle */}
              <button
                onClick={toggleSidebar}
                title={t("leftPanel.expandSidebar")}
                className="w-full h-[42px] flex items-center justify-center text-gray-500 hover:bg-gray-300 hover:text-gray-800 border-b border-gray-300 transition-colors"
              >
                <Icon name="arrowright" size={16} color="currentColor" />
              </button>
              {setupSubmenus
                .filter((s: any) => !DELETED_MENU_ITEMS.includes(s.menuCode))
                .map((s: any) => {
                  const isDisabled =
                    (!s.read && !s.write && !s.delete) ||
                    DISABLED_MENU_ITEMS.includes(s.menuCode);
                  return (
                    <button
                      key={s.menuId}
                      disabled={isDisabled}
                      className={`w-full h-[50px] flex items-center justify-center text-gray-700 hover:bg-gray-300 ${
                        selectedSetupSubmenu?.menuId === s.menuId ? "bg-white" : ""
                      } ${
                        isDisabled ? "text-gray-400 cursor-not-allowed opacity-40" : "text-gray-800"
                      }`}
                      onClick={(e) => handleIconClick(s, e)}
                    >
                      <div title={getMenuDisplayName(s, t)} className="w-full flex items-center justify-center">
                        <Icon
                          name={(s.icon || "settings") as iconNames}
                          size={20}
                          color="#000"
                          title={getMenuDisplayName(s, t)}
                        />
                      </div>
                    </button>
                  );
                })}

              {/* Exit Settings */}
              <button
                className="w-full h-[50px] flex items-center justify-center text-red-600 hover:bg-red-100"
                onClick={exitSetupMode}
              >
                <div title={t("leftPanel.exitSettings")} className="w-full flex items-center justify-center">
                  <Icon title={t("leftPanel.exitSettings")} name="exit-settings" size={20} color="#dc2626" />
                </div>
              </button>
            </div>

          </>
        ) : (
          // ── Normal mode: icon column only (collapsed, no labels)
          <>
            <div className="w-full h-full bg-gray-200">
              {/* Expand toggle */}
              <button
                onClick={toggleSidebar}
                title={t("leftPanel.expandSidebar")}
                className="w-full h-[42px] flex items-center justify-center text-gray-500 hover:bg-gray-300 hover:text-gray-800 border-b border-gray-300 transition-colors"
              >
                <Icon name="arrowright" size={16} color="currentColor" />
              </button>
              {visibleMenus.map((menu: any) => {
                const isDisabled =
                  (!menu.read && !menu.write && !menu.delete) ||
                  DISABLED_MENU_ITEMS.includes(menu.menuCode);
                const isAIMenu = menu.menuCode === "HUBWALLET_AI";

                return (
                  <button
                    key={menu.menuId}
                    disabled={isDisabled}
                    className={`w-full h-[50px] flex items-center justify-center relative overflow-hidden ${
                      isAIMenu
                        ? `bg-gradient-to-r from-[#7B5CFF] to-[#1ED3FF] text-white hover:from-[#6A4AF5] hover:to-[#14C4EE] transition-all duration-200 ${
                            selectedMenu?.menuId === menu.menuId
                              ? "shadow-lg shadow-[#7B5CFF]/40"
                              : "shadow-md shadow-[#7B5CFF]/20"
                          }`
                        : `text-gray-700 hover:bg-gray-300 ${
                            selectedMenu?.menuId === menu.menuId ? "bg-white" : ""
                          }`
                    } ${
                      isDisabled
                        ? "text-gray-400 cursor-not-allowed opacity-40"
                        : isAIMenu
                        ? "text-white"
                        : "text-gray-800"
                    }`}
                    onClick={(e) => {
                      // Settings → enter settings mode (original behaviour)
                      if (menu.menuCode === SETTINGS_MENU_CODE) {
                        handleMenuClick(menu);
                      } else {
                        handleIconClick(menu, e);
                      }
                    }}
                    style={
                      isAIMenu
                        ? { backgroundSize: "200% 100%", animation: "gradientShift 3s ease infinite" }
                        : {}
                    }
                  >
                    {isAIMenu && (
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-30 animate-pulse" />
                    )}
                    <div title={getMenuDisplayName(menu, t)} className="w-full flex items-center justify-center z-10">
                      <Icon
                        name={(menu.icon || "settings") as iconNames}
                        size={20}
                        color={isAIMenu ? "#FFFFFF" : "#000"}
                        title={getMenuDisplayName(menu, t)}
                      />
                    </div>
                  </button>
                );
              })}
            </div>

          </>
        )}
      </div>

      {/* Floating submenu panel (collapsed + non-Settings items) */}
      {floatingPanel && (
        <FloatingSubmenu
          menu={floatingPanel.menu}
          anchorTop={floatingPanel.top}
          sidebarWidth={COLLAPSED_W}
          onClose={() => setFloatingPanel(null)}
          onNavigate={(url) => navigateWithConfirmation(url)}
          DISABLED_MENU_ITEMS={DISABLED_MENU_ITEMS}
          DELETED_MENU_ITEMS={DELETED_MENU_ITEMS}
          pathname={location.pathname}
        />
      )}
    </div>
  );
};

export default LeftPanel;
